import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';

import { AuditoriaService } from '../services/AuditoriaService';
import { IEmpresaAuditoria, IPeriodoAuditoria } from '../interfaces/IAuditoria';

import { CATEGORIAS_CIRCULARIZACAO, IIndicadoresCircularizacao } from './interfaces/ICircularizacao';
import { CircularizacaoService } from './services/CircularizacaoService';
import { categoriaParaSlug } from './utils/categoriaSlug';

import './styles/circularizacao.css';

interface LocationState {
  empresaId?: number;
  periodo?: string;
}

interface IOption<T> {
  value: T;
  label: string;
}

const ICONE_POR_CATEGORIA: Record<string, string> = {
  FORNECEDORES: 'bi-truck',
  BANCOS: 'bi-bank',
  JURIDICO: 'bi-briefcase',
  CLIENTES: 'bi-people',
  PARTES_RELACIONADAS: 'bi-diagram-3',
};

// Prompt Mestre V3.0 — "Central de Circularizações" (seção 8 — Gestão das Cinco Categorias).
// Empresa e Período usam o mesmo serviço real já existente no Revisor Contábil. Os indicadores e a
// listagem de cada categoria vêm do Back-End real (CircularizacaoService -> api/circularizacao),
// escopados por período (esteira) — por isso é preciso Empresa E Período selecionados, não só
// Empresa (o mock anterior ignorava o período; o Back-End real não pode, já que o processo de
// circularização é por período de auditoria).
const CircularizacaoCentralPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();

  // Mesmo padrão de AuditoriaPage para "voltando de uma sub-tela com empresa/período já
  // escolhidos": a seleção vinda de location.state fica guardada numa ref (nunca pré-popula o
  // estado síncrono) e só vira estado de verdade depois que a lista correspondente carregou e o
  // rótulo existe — assim o Select nunca chega a renderizar o id cru (ex.: "22982") nem um rótulo
  // em branco antes da empresa/período serem resolvidos.
  const selecaoInicial = useRef(location.state);

  const [empresas, setEmpresas] = useState<IEmpresaAuditoria[]>([]);
  const [periodos, setPeriodos] = useState<IPeriodoAuditoria[]>([]);
  const [empresaId, setEmpresaId] = useState<number | undefined>();
  const [periodo, setPeriodo] = useState<string | undefined>();
  const [indicadoresPorCategoria, setIndicadoresPorCategoria] = useState<
    Partial<Record<string, IIndicadoresCircularizacao>>
  >({});
  const [carregandoIndicadores, setCarregandoIndicadores] = useState(false);

  useEffect(() => {
    AuditoriaService.listarEmpresas()
      .then((lista) => {
        setEmpresas(lista);
        const inicial = selecaoInicial.current?.empresaId;
        if (inicial && lista.some((e) => e.id === inicial)) {
          setEmpresaId(inicial);
        }
      })
      .catch(() => setEmpresas([]));
  }, []);

  useEffect(() => {
    if (!empresaId) {
      setPeriodos([]);
      return;
    }
    AuditoriaService.listarPeriodos(empresaId)
      .then((lista) => {
        setPeriodos(lista);
        const inicial =
          selecaoInicial.current?.empresaId === empresaId ? selecaoInicial.current?.periodo : undefined;
        if (inicial && lista.some((p) => String(p.esteiraId) === inicial)) {
          setPeriodo(inicial);
        }
      })
      .catch(() => setPeriodos([]));
  }, [empresaId]);

  useEffect(() => {
    if (!periodo) {
      setIndicadoresPorCategoria({});
      return;
    }
    const esteiraId = Number(periodo);
    setCarregandoIndicadores(true);
    Promise.all(
      CATEGORIAS_CIRCULARIZACAO.map(async (c) => [c.value, await CircularizacaoService.obterIndicadores(esteiraId, c.value)] as const)
    ).then((entradas) => {
      setIndicadoresPorCategoria(Object.fromEntries(entradas));
      setCarregandoIndicadores(false);
    });
  }, [periodo]);

  const empresaSelecionada = empresas.find((e) => e.id === empresaId);
  const periodoSelecionado = periodos.find((p) => String(p.esteiraId) === periodo);

  const abrirCategoria = (categoria: (typeof CATEGORIAS_CIRCULARIZACAO)[number]['value']) => {
    history.push({
      pathname: `/auditoria/circularizacao/${categoriaParaSlug(categoria)}`,
      state: {
        empresaId,
        periodo,
        empresaNome: empresaSelecionada?.nomeFantasia || empresaSelecionada?.razaoSocial,
        periodoDescricao: periodoSelecionado?.descricao,
      },
    });
  };

  return (
    <div className='auditoria-page'>
      <div className='auditoria-header-card'>
        <div className='d-flex justify-content-between align-items-start mb-5'>
          <div>
            <button type='button' className='btn btn-sm btn-light mb-3' onClick={() => history.push('/auditoria')}>
              <i className='bi bi-arrow-left me-1' /> Voltar para Auditoria
            </button>
            <h2 className='auditoria-title'>Central de Circularizações</h2>
            <p className='auditoria-subtitle'>
              Seleção revisável de destinatários, cadastro de contatos, planejamento de prazos e impressão de
              cartas de confirmação externa.
            </p>
          </div>
        </div>

        <div className='row g-3'>
          <div className='col-md-6'>
            <label className='form-label fw-bold fs-8'>Empresa</label>
            <Select<IOption<number>>
              inputId='circularizacao-select-empresa'
              classNamePrefix='audit-select'
              placeholder='Selecione a empresa...'
              options={empresas.map((e) => ({ value: e.id, label: e.nomeFantasia || e.razaoSocial || e.cnpj }))}
              value={
                empresaId
                  ? { value: empresaId, label: empresaSelecionada?.nomeFantasia || empresaSelecionada?.razaoSocial || '' }
                  : null
              }
              onChange={(opt: SingleValue<IOption<number>>) => {
                setEmpresaId(opt?.value);
                setPeriodo(undefined);
              }}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-bold fs-8'>Período</label>
            <Select<IOption<string>>
              inputId='circularizacao-select-periodo'
              classNamePrefix='audit-select'
              placeholder='Selecione o período...'
              isDisabled={!empresaId}
              options={periodos.map((p) => ({ value: String(p.esteiraId), label: p.descricao }))}
              value={periodo ? { value: periodo, label: periodoSelecionado?.descricao ?? periodo } : null}
              onChange={(opt: SingleValue<IOption<string>>) => setPeriodo(opt?.value)}
            />
          </div>
        </div>

        {!empresaId && (
          <div className='mt-4 audit-message audit-message-info' style={{ display: 'flex', gap: 12, padding: '14px 16px', borderRadius: 10 }}>
            <i className='bi bi-info-circle-fill' style={{ fontSize: 18 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Selecione uma Empresa para continuar.</div>
            </div>
          </div>
        )}

        {empresaId && !periodo && (
          <div className='mt-4 audit-message audit-message-info' style={{ display: 'flex', gap: 12, padding: '14px 16px', borderRadius: 10 }}>
            <i className='bi bi-info-circle-fill' style={{ fontSize: 18 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Selecione um Período para continuar.</div>
            </div>
          </div>
        )}
      </div>

      {empresaId && periodo && (
        <div className='circ-categorias-grid'>
          {CATEGORIAS_CIRCULARIZACAO.map((categoria) => {
            const ind = indicadoresPorCategoria[categoria.value];
            return (
              <div key={categoria.value} className='circ-categoria-card'>
                <i className={`bi ${ICONE_POR_CATEGORIA[categoria.value]} circ-categoria-icon`} />
                <div className='circ-categoria-titulo'>{categoria.label}</div>
                <div className='circ-categoria-descricao'>{categoria.descricaoTemplate}</div>
                {carregandoIndicadores && !ind && <div className='text-muted' style={{ fontSize: 12 }}>Carregando...</div>}
                {ind && (
                  <div className='circ-categoria-stats'>
                    <span><strong>{ind.empresasAtivas}</strong> ativas</span>
                    <span><strong>{ind.empresasIncluidasManualmente}</strong> incluídas</span>
                    <span><strong>{ind.empresasExcluidas}</strong> excluídas</span>
                    <span><strong>{ind.cartasPreparadas}</strong> cartas prontas</span>
                    <span><strong>{ind.cartasPendentes}</strong> pendências</span>
                  </div>
                )}
                <button type='button' className='btn btn-sm btn-primary mt-2' onClick={() => abrirCategoria(categoria.value)}>
                  Gerenciar Circularizações
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CircularizacaoCentralPage;
